import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { ScrollArea } from '../ui/scroll-area';
import { Button } from '../ui/button';
import { GitBranch, RefreshCw, UserMinus, User } from 'lucide-react';
import { AdminAuditDialog } from './AdminAuditDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ReferralChainList — "who have I vouched for, directly and indirectly"
 * tree view. Critical for chain-of-trust visibility: if a bad actor
 * slips through, you can ask the Invites tab "show me everyone
 * downstream of me" and spot the branch.
 *
 * Layout: a flat nested list grouped by depth. Deleted members render
 * as tombstones (grayed out, "former member") — their descendants stay
 * visible because the backend preserves a PII-free tombstone row.
 */

const fmtAge = (iso) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
};

export const ReferralChainList = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchTree = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/users/me/referrals`);
      setData(res.data);
    } catch (e) {
      toast.error('Could not load referral chain');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTree();
  }, []);

  // Build a tree structure indexed by parent id for rendering nested branches.
  const { byParent, depth1 } = useMemo(() => {
    if (!data) return { byParent: {}, depth1: [] };
    const byParent = {};
    for (const n of data.nodes) {
      const p = n.referred_by || data.root;
      (byParent[p] = byParent[p] || []).push(n);
    }
    for (const key of Object.keys(byParent)) {
      byParent[key].sort((a, b) => (a.joined_at || '').localeCompare(b.joined_at || ''));
    }
    return { byParent, depth1: byParent[data.root] || [] };
  }, [data]);

  const renderNode = (node, level = 0) => {
    const children = byParent[node.id] || [];
    const indent = { paddingLeft: `${level * 16}px` };
    return (
      <div key={node.id}>
        <div
          className="flex items-center gap-2 py-1.5 px-2 rounded text-xs hover:bg-secondary/20"
          style={indent}
          data-testid={`referral-node-${node.id}`}
        >
          {node.deleted ? (
            <>
              <UserMinus className="w-3.5 h-3.5 text-slate-600 shrink-0" />
              <span className="text-slate-600 italic flex-1 min-w-0 truncate">
                former member
              </span>
              <span className="text-[10px] text-slate-700">
                {fmtAge(node.joined_at)}
              </span>
            </>
          ) : (
            <>
              <User className="w-3.5 h-3.5 text-primary/70 shrink-0" />
              <span className="text-slate-200 font-mono flex-1 min-w-0 truncate">
                {node.username}
              </span>
              {node.direct_referrals > 0 && (
                <span
                  className="text-[10px] text-slate-500"
                  title={`${node.direct_referrals} direct referral${node.direct_referrals === 1 ? '' : 's'}`}
                >
                  +{node.direct_referrals}
                </span>
              )}
              <span className="text-[10px] text-slate-600">
                {fmtAge(node.joined_at)}
              </span>
            </>
          )}
        </div>
        {children.map((c) => renderNode(c, level + 1))}
      </div>
    );
  };

  return (
    <div className="space-y-2" data-testid="referral-chain-list">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <GitBranch className="w-4 h-4 text-slate-400" />
          <label className="text-sm text-slate-300">Your referral chain</label>
          {data && data.total > 0 && (
            <span
              className="text-xs text-slate-500"
              data-testid="referral-chain-summary"
            >
              · {data.direct} direct · {data.total} total
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <AdminAuditDialog
            isAdmin={!!user?.is_admin}
            isOwner={!!user?.is_owner}
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchTree}
            disabled={loading}
            data-testid="referral-chain-refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      <ScrollArea className="h-40 border border-border rounded-lg">
        {!data || depth1.length === 0 ? (
          <div className="py-8 text-center text-xs text-slate-500">
            <GitBranch className="w-6 h-6 mx-auto mb-2 opacity-40" />
            {loading ? 'Loading...' : "You haven't vouched for anyone yet."}
            <p className="mt-1 opacity-70 max-w-[240px] mx-auto leading-relaxed">
              Mint an invite code or approve a waitlist request — they'll show up here.
            </p>
          </div>
        ) : (
          <div className="p-1">{depth1.map((n) => renderNode(n, 0))}</div>
        )}
      </ScrollArea>
      {data && data.total > 0 && (
        <p className="text-[10px] text-slate-600 leading-relaxed">
          This chain is private — only visible to you. Former members appear as
          tombstones so descendants remain traceable without leaking any PII.
        </p>
      )}
    </div>
  );
};

export default ReferralChainList;
